import { connect } from 'react-redux'
import some from 'lodash/some'

import { updateFilters } from 'actions/searchActions'
import Spinner from 'components/display/Spinner'
import { usePlaceLookupLazyQuery } from 'hooks/useQueries'
import LocationItem from './ResultItem/LocationItem'
import EscapeItem from './ResultItem/EscapeItem'
import { SpinnerWrapper } from './styles'
import { ResultListProps, SearchableCountriesProps } from '../types'

const searchPagePathname = '/listings'

interface RegionFilterProps {
  country: string
  location: string
  regions: string[]
  state: string
}

interface ResultsProps {
  dispatch?: (...args: any) => any
  onSelect: () => any
  searchableCountries?: SearchableCountriesProps[]
  results: ResultListProps
  loading: boolean
  sessionToken: string
}

const ResultsList = ({
  dispatch,
  onSelect,
  searchableCountries,
  results,
  loading,
  sessionToken,
}: ResultsProps) => {
  const onItemClick = (url: string) => {
    const win: Window = window
    win.location = url
  }

  const [lookupPlace] = usePlaceLookupLazyQuery()

  const onPredictionClick = (prediction: GQL_Prediction): void => {
    lookupPlace({
      variables: {
        googlePlaceId: prediction.googlePlaceId,
        sessionToken,
      },
    })
      .then(({ data }) => {
        const place = data?.placeLookup
        if (place?.bounds) {
          const newFilters = {
            location: place.name,
            country: place.country.code,
            origin: place.origin,
            sw: place.bounds.sw,
            ne: place.bounds.ne,
          }
          dispatch(updateFilters(newFilters, searchPagePathname))
          onSelect()
        }
      })
      .catch((): void => {
        /* ignore place-lookup failures */
      })
  }

  const onRegionClick = (newFilters: RegionFilterProps) => {
    dispatch(updateFilters(newFilters, searchPagePathname))
    onSelect()
  }

  if (loading) {
    return (
      <SpinnerWrapper>
        <Spinner />
      </SpinnerWrapper>
    )
  }

  if (!some(results)) {
    return (
      <SpinnerWrapper>
        <em>No results</em>
      </SpinnerWrapper>
    )
  }

  return (
    <div>
      {results.map(({ result }) => {
        switch (result.__typename) {
          case 'Prediction':
            return (
              <LocationItem
                key={`${result.__typename}:${result.googlePlaceId}`}
                title={result.description}
                onClick={() => onPredictionClick(result)}
              />
            )
          case 'Region': {
            const regionName = `${result.name} (Region)`
            const countryId = result.state?.countryId?.toString()
            const country = searchableCountries?.find(
              ({ id }) => id === countryId
            )
            const title = [regionName, result.state?.code, country?.code]
              .filter(Boolean)
              .join(', ')

            return (
              <LocationItem
                key={`${result.__typename}:${result.id}`}
                title={title}
                onClick={() =>
                  onRegionClick({
                    location: regionName,
                    state: result.state?.code,
                    regions: [result.slug],
                    country: country?.code,
                  })
                }
              />
            )
          }
          case 'Story':
            return (
              <EscapeItem
                key={`${result.__typename}:${result.id}`}
                heading={`Story by ${result.user?.firstName}`}
                title={result.title}
                subtitle={result.featuredListing?.location?.name}
                image={result.promoImage?.thumb}
                url={result.url}
                onClick={onItemClick}
              />
            )
          case 'Listing':
            return (
              <EscapeItem
                key={`${result.__typename}:${result.id}`}
                heading="Listing"
                title={result.title}
                subtitle={result.location?.name}
                image={result.promoImage?.thumb}
                url={result.url}
                onClick={onItemClick}
              />
            )
          default:
            return ''
        }
      })}
    </div>
  )
}

export default connect()(ResultsList)
